// src/EventHandlers.js
// ✅ Complete Event Handlers (clean + generic loader click)
// - Drag / swipe to rotate pano
// - Click / tap loaders (hotspots) by projecting their 3D position to screen
// - Resize updates both renderers
//
// NOTE: This version does NOT need any hardcoded view mapping.
// It will call loader.onClick() for whichever loader is tapped/clicked.

export const setupEventHandlers = ({
  camera,
  clickableObjects, // { key: { position: THREE.Vector3, onClick: fn, element? } }
  renderer,
  cssRenderer,
  onInteractionStart,
  onInteractionEnd,
}) => {
  const state = {
    isUserInteracting: false,
    targetLon: 0,
    targetLat: 0,
    currentLon: 0,
    currentLat: 0,
    onPointerDownPointerX: 0,
    onPointerDownPointerY: 0,
    onPointerDownLon: 0,
    onPointerDownLat: 0,
  };

  const maxLat = 85;
  const movementSpeed = 0.3;
  const CLICK_RADIUS = 45; // slightly forgiving for mobile

  // ---------- Mouse Drag ----------
  const onPointerDown = (event) => {
    // ignore right/middle click
    if (event.button !== undefined && event.button !== 0) return;

    state.isUserInteracting = true;
    state.onPointerDownPointerX = event.clientX;
    state.onPointerDownPointerY = event.clientY;
    state.onPointerDownLon = state.targetLon;
    state.onPointerDownLat = state.targetLat;

    if (onInteractionStart) onInteractionStart();
  };

  const onPointerMove = (event) => {
    if (!state.isUserInteracting) return;

    state.targetLon =
      (event.clientX - state.onPointerDownPointerX) * movementSpeed +
      state.onPointerDownLon;

    const newLat =
      (event.clientY - state.onPointerDownPointerY) * movementSpeed +
      state.onPointerDownLat;

    state.targetLat = Math.max(-maxLat, Math.min(maxLat, newLat));
  };

  const onPointerUp = () => {
    state.isUserInteracting = false;
    if (onInteractionEnd) onInteractionEnd();
  };

  // ---------- Touch Drag ----------
  const onTouchStart = (event) => {
    if (!event.touches || event.touches.length !== 1) return;

    state.isUserInteracting = true;
    state.onPointerDownPointerX = event.touches[0].pageX;
    state.onPointerDownPointerY = event.touches[0].pageY;
    state.onPointerDownLon = state.targetLon;
    state.onPointerDownLat = state.targetLat;

    if (onInteractionStart) onInteractionStart();
  };

  const onTouchMove = (event) => {
    if (!event.touches || event.touches.length !== 1) return;
    if (!state.isUserInteracting) return;

    state.targetLon =
      (event.touches[0].pageX - state.onPointerDownPointerX) * movementSpeed +
      state.onPointerDownLon;

    const newLat =
      (event.touches[0].pageY - state.onPointerDownPointerY) * movementSpeed +
      state.onPointerDownLat;

    state.targetLat = Math.max(-maxLat, Math.min(maxLat, newLat));
  };

  const onTouchEnd = () => {
    state.isUserInteracting = false;
    if (onInteractionEnd) onInteractionEnd();
  };

  // ---------- Loader Click / Tap ----------
  // We compute projected screen position of each loader and check distance to click.
  const onDocumentClick = (event) => {
    // In some mobile browsers, touchend can trigger click with no clientX/clientY.
    if (event.clientX == null || event.clientY == null) return;

    const clickX = event.clientX;
    const clickY = event.clientY;

    const objects = clickableObjects || {};
    for (const loader of Object.values(objects)) {
      if (!loader || !loader.position) continue;

      // Optional: if the loader DOM is hidden, skip
      if (loader.element && loader.element.style?.display === "none") continue;

      const vector = loader.position.clone().project(camera);

      // If behind camera, ignore
      if (vector.z > 1) continue;

      const screenX = (vector.x * 0.5 + 0.5) * window.innerWidth;
      const screenY = (-vector.y * 0.5 + 0.5) * window.innerHeight;

      const dx = clickX - screenX;
      const dy = clickY - screenY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= CLICK_RADIUS) {
        // stop here so we don't trigger multiple
        event.preventDefault();
        event.stopPropagation();

        if (typeof loader.onClick === "function") {
          loader.onClick();
        }
        break;
      }
    }
  };

  // ---------- Resize ----------
  const onWindowResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;

    camera.aspect = w / h;
    camera.updateProjectionMatrix();

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);

    cssRenderer.setSize(w, h);
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onDocumentClick,
    onWindowResize,
    state,
  };
};
